import loadable from '@loadable/component'
import Helmet from 'react-helmet'

import { Emoji, Image, Media } from '@tribeplatform/gql-client/types'
import { getDomainInfo } from '@tribeplatform/react-components/common'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'

import { AppInitialProps } from '../@types'
import { isIOS } from '../utils/device'
import { truncate } from '../utils/string'

const IMGIX_SQUARE_IMAGE_PARAMS = '&ar=1:1&fit=crop'

const EmojiMartData = loadable.lib(() => import('@emoji-mart/data'))

const getIcon = (
  media?: Media,
  size: keyof Image['urls'] = 'thumb',
  emojiData = undefined,
  subfolder?: string,
) => {
  if (media && (media as any).__typename === 'Image') {
    const img = media as Image
    return `${img.urls[size]}${IMGIX_SQUARE_IMAGE_PARAMS}`
  }
  if (media && (media as any)?.__typename === 'Emoji') {
    try {
      const emojiText = (media as Emoji).text
      const emoji = emojiData?.emojis?.[emojiText]
      const nativeEmoji = emoji?.skins[0].native
      if (nativeEmoji) {
        const svg = `<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>${nativeEmoji}</text></svg>`
        return `data:image/svg+xml,${svg}`
      }
    } catch (e) {
      // ignore
    }
  }
  return `${subfolder}/favicon.ico`
}

export const GenericMetaDumb = ({ authToken, url }) => {
  const { $t } = useI18n()
  const { path } = url
  const { networkPublicInfo, network } = authToken

  if (!networkPublicInfo) {
    return null
  }

  const { domain, subfolder } = getDomainInfo(networkPublicInfo)

  let title = networkPublicInfo?.name || ''
  title = truncate(title, 50)

  let description =
    network?.description ||
    $t(
      {
        defaultMessage:
          'Join {community_name} to start sharing and connecting with like-minded people.',
        id: 'Meta.DefaultDescription',
      },
      {
        community_name: networkPublicInfo.name,
      },
    )

  description = truncate(description, 160)

  const brandColor = network?.brandColor || '#FFFFFF'
  const icon: Media = networkPublicInfo?.favicon

  const canonicalUrl = path ? `https://${domain}${subfolder}${path}` : undefined

  return (
    <EmojiMartData>
      {data => (
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />
          <link rel="icon" href={getIcon(icon, 'thumb', data, subfolder)} />
          <link
            rel="icon"
            sizes="192x192"
            href={getIcon(icon, 'small', data, subfolder)}
          />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href={getIcon(icon, 'thumb', data, subfolder)}
          />

          <meta name="application-name" content={title} />
          <meta name="apple-mobile-web-app-title" content={title} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />

          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:url" content={canonicalUrl} />
          <meta name="twitter:url" content={canonicalUrl} />

          {/*
            IE
            Reference: https://learn.microsoft.com/en-us/previous-versions/windows/internet-explorer/ie-developer/platform-apis/dn255024(v=vs.85)
           */}
          <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
          <meta name="msapplication-navbutton-color" content={brandColor} />
          <meta name="msapplication-tap-highlight" content="no" />
          <meta name="msapplication-TileColor" content={brandColor} />
          <meta
            name="msapplication-TileImage"
            content={getIcon(icon, 'thumb', data, subfolder)}
          />

          {/* Add to home-screen for Chrome on Android */}
          <meta name="mobile-web-app-capable" content="yes" />
          <meta name="theme-color" content={brandColor} />

          {/* iOS */}
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta
            name="apple-mobile-web-app-status-bar-style"
            content="default"
          />
          <meta name="apple-touch-fullscreen" content="yes" />
          {isIOS() && (
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
            />
          )}
          <meta name="format-detection" content="telephone=no" />
          {path === '/' && (
            <link
              rel="alternate"
              type="application/rss+xml"
              title={networkPublicInfo?.name}
              href={`https://${domain}${subfolder}/rss/feed`}
            />
          )}
          <link rel="manifest" href={`${subfolder}/manifest.json`} />
        </Helmet>
      )}
    </EmojiMartData>
  )
}

export const GenericMeta = ({ pageProps }: { pageProps: AppInitialProps }) => {
  const { data: authToken } = useAuthToken()

  return (
    <GenericMetaDumb
      authToken={authToken || pageProps.authToken || {}}
      url={pageProps.url}
    />
  )
}
